import React, { useContext } from "react"

import { useTranslation } from "react-i18next"

import { Card, CardHeader, CircularProgress, Grid } from "@mui/material"
import { Button, NotificationUtils, Snackbar, Typography } from "@synapse-analytics/synapse-ui"
import { useFormikContext } from "formik"

import { MonacoTextEditor } from "../../../../components/data-display/MonacoTextEditor"
import { Context } from "../../Context"
import type { DryRunFormProps, DryRunFormPropsFormikValues } from "../../Interfaces"

import styles from "./DryRunForm.module.scss"

/**
 * Dry run form
 * @return {React.ReactElement}
 */
export function DryRunForm(props: Readonly<DryRunFormProps>): React.ReactElement {
  const {
    response,
    requestErrorMessage,
    isRequestError,
    isMutationError,
    responseStatusCode,
    isLoadingRequest,
    enableSideBar = false,
  } = props

  const { isSidebarOpen, setIsSidebarOpen } = useContext(Context)

  const { values, setFieldValue } = useFormikContext<DryRunFormPropsFormikValues>()
  const { t } = useTranslation("ModelConfigurations")

  return (
    <Grid container item xs={12} mt={0} gap={2}>
      <Grid item xs={12} display={"flex"} justifyContent={"space-between"} alignItems={"center"}>
        <Typography variant="h3-bold">{t("forms.dryRun.header")}</Typography>
        {enableSideBar && !isSidebarOpen && (
          <Button variant="secondary" onClick={setIsSidebarOpen.bind(null, (prev) => !prev)}>
            {isSidebarOpen ? t("forms.dryRun.close") : t("forms.dryRun.open")} {t("forms.dryRun.history")}
          </Button>
        )}
      </Grid>

      <Grid item xs={12} mt={0.5}>
        <Grid container spacing={2} direction="column">
          {isRequestError || isMutationError ? (
            <Grid item>
              <Snackbar
                severity="error"
                fullWidth
                description={
                  isRequestError && isMutationError && responseStatusCode !== 406
                    ? requestErrorMessage
                    : requestErrorMessage
                      ? requestErrorMessage + ". " + t("forms.dryRun.errors.adjustAndRetry")
                      : t("forms.dryRun.errors.generic")
                }
              />
            </Grid>
          ) : (
            response !== "// Execute to see response" && (
              <Grid item>
                <Snackbar severity="success" fullWidth description={t("forms.dryRun.success")} />
              </Grid>
            )
          )}

          <Grid container item spacing={2} direction="row">
            <Grid item xs={6}>
              <Card className={styles.monacoCard}>
                <CardHeader
                  title={<Typography variant="p">{t("forms.dryRun.request")}</Typography>}
                  className={styles.responseTextArea}
                />
                {isLoadingRequest ? (
                  <Grid
                    container
                    item
                    xs={12}
                    alignItems={"center"}
                    justifyContent={"center"}
                    style={{ height: "60vh" }}
                  >
                    <CircularProgress style={{ color: "rgb(25 118 210)" }} size={36} />
                  </Grid>
                ) : (
                  <MonacoTextEditor
                    value={isLoadingRequest ? undefined : values.request}
                    language={"json"}
                    height={"60vh"}
                    onChange={(text) => setFieldValue("request", text)}
                    loading={<CircularProgress style={{ color: "rgb(25 118 210)" }} size={36} />}
                    borderRadius={"0 0 8px 8px"}
                  />
                )}
              </Card>
            </Grid>

            <Grid item xs={6}>
              <Card className={styles.monacoCard}>
                <CardHeader
                  title={<Typography variant="p">{t("forms.dryRun.response")}</Typography>}
                  action={
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => {
                        navigator.clipboard.writeText(response)
                        NotificationUtils.toast(t("toasts.copied"), { severity: "success" })
                      }}
                    >
                      {t("forms.dryRun.copy")}
                    </Button>
                  }
                  className={styles.responseTextArea}
                />
                <MonacoTextEditor
                  value={response}
                  language={"json"}
                  height={"60vh"}
                  readOnly
                  loading={<CircularProgress style={{ color: "rgb(25 118 210)" }} size={36} />}
                  borderRadius={"0 0 8px 8px"}
                />
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}
